import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, imgUrl } from "../lib/api";
import { ProductCard } from "../components/ProductCard";
import { QuickView } from "../components/QuickView";
import { Reveal } from "../components/Reveal";
import { Seo } from "../components/Seo";

export default function CategoryPage() {
  const { slug } = useParams();
  const [category, setCategory] = useState(null);
  const [data, setData] = useState({ items: [], total: 0, pages: 1 });
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState("new");
  const [loading, setLoading] = useState(true);
  const [quick, setQuick] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setPage(1);
    setNotFound(false);
    api.get("/categories").then((r) => {
      const found = r.data.find((c) => c.slug === slug);
      setCategory(found || null);
      setNotFound(!found);
    }).catch(() => setNotFound(true));
  }, [slug]);

  useEffect(() => {
    if (notFound) return;
    setLoading(true);
    api.get("/products", { params: { category: slug, sort, page, limit: 12 } })
      .then((r) => setData(r.data)).catch(() => setData({ items: [], total: 0, pages: 1 }))
      .finally(() => setLoading(false));
  }, [slug, sort, page, notFound]);

  if (notFound) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-28 text-center" data-testid="category-not-found">
        <Seo title="Category not found — Anaya Fashion & Gifts" path="/shop" noindex />
        <h1 className="font-display text-4xl">This shelf is empty</h1>
        <p className="mt-3 text-sm font-light text-muted-foreground">We couldn&#8217;t find that category.</p>
        <Link to="/shop" className="mt-6 inline-block rounded-full bg-foreground px-7 py-3 text-xs tracking-[0.2em] text-white uppercase hover:bg-primary">Shop all</Link>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6" data-testid="category-page">
      {category && (
        <Seo
          title={`${category.name} — Anaya Fashion & Gifts`}
          description={(category.description || `Shop ${category.name} at Anaya Fashion & Gifts — hand-picked pieces, hand-wrapped parcels.`).slice(0, 158)}
          path={`/category/${category.slug}`}
          image={category.image_url || undefined}
          jsonLd={{ "@context": "https://schema.org", "@type": "BreadcrumbList", "itemListElement": [
            { "@type": "ListItem", position: 1, name: "Home", item: "https://anayafashion.store/" },
            { "@type": "ListItem", position: 2, name: "Shop", item: "https://anayafashion.store/shop" },
            { "@type": "ListItem", position: 3, name: category.name },
          ] }}
        />
      )}
      <Reveal>
        <div className="relative overflow-hidden rounded-3xl border border-white/70 bg-gradient-to-br from-secondary/70 via-white/70 to-accent/50 px-6 py-12 sm:px-12">
          {category?.image_url && <img src={imgUrl(category.image_url)} alt="" className="absolute inset-0 h-full w-full object-cover opacity-25" />}
          <p className="relative text-[11px] tracking-[0.4em] text-primary uppercase">Category</p>
          <h1 className="relative mt-2 font-display text-4xl sm:text-5xl" data-testid="category-name">{category?.name || "…"}</h1>
          <p className="relative mt-3 max-w-xl text-sm font-light text-muted-foreground">{category?.description}</p>
          <p className="relative mt-4 text-xs tracking-[0.25em] text-muted-foreground uppercase" data-testid="category-count">{data.total} pieces</p>
        </div>
      </Reveal>

      <div className="mt-8 flex justify-end">
        <select data-testid="category-sort" value={sort} onChange={(e) => { setSort(e.target.value); setPage(1); }}
          className="rounded-full border border-input bg-white/70 px-4 py-2 text-xs tracking-widest uppercase outline-none">
          <option value="new">Newest</option>
          <option value="price_asc">Price: low to high</option>
          <option value="price_desc">Price: high to low</option>
          <option value="name">Name A–Z</option>
        </select>
      </div>

      {loading ? (
        <div className="mt-6 grid grid-cols-2 gap-x-4 gap-y-8 md:grid-cols-3 lg:grid-cols-4">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="animate-pulse"><div className="aspect-[3/4] rounded-[1.25rem] bg-secondary/60" /><div className="mt-3 h-4 w-3/4 rounded bg-secondary/60" /></div>
          ))}
        </div>
      ) : data.items.length === 0 ? (
        <div className="glass mt-6 rounded-3xl py-20 text-center" data-testid="category-empty">
          <p className="font-display text-2xl">Coming soon</p>
          <p className="mt-2 text-sm font-light text-muted-foreground">We&#8217;re curating pieces for this shelf. Check back shortly — or browse everything else.</p>
          <Link to="/shop" className="mt-6 inline-block rounded-full border border-foreground/20 px-7 py-3 text-xs tracking-[0.2em] uppercase hover:border-primary hover:text-primary">Shop all</Link>
        </div>
      ) : (
        <div className="mt-6 grid grid-cols-2 gap-x-4 gap-y-8 md:grid-cols-3 lg:grid-cols-4" data-testid="category-grid">
          {data.items.map((p, i) => <ProductCard key={p.id} product={p} index={i} onQuickView={setQuick} />)}
        </div>
      )}

      {data.pages > 1 && (
        <div className="mt-10 flex justify-center gap-2" data-testid="category-pagination">
          {Array.from({ length: data.pages }).map((_, i) => (
            <button key={i} data-testid={`cat-page-btn-${i + 1}`} onClick={() => setPage(i + 1)}
              className={`h-9 w-9 rounded-full text-sm transition-colors ${page === i + 1 ? "bg-primary text-white" : "border border-input bg-white/70 hover:bg-secondary"}`}>
              {i + 1}
            </button>
          ))}
        </div>
      )}

      <QuickView product={quick} onClose={() => setQuick(null)} />
    </div>
  );
}
